import React, { useEffect, useRef, RefObject } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import FilterSidebar from "./FilterSidebar";
import Icon from "../../../../../../common/components/Icon/Icon";

interface FilterSidebarOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  idPrefix: string;
  triggerRef: RefObject<HTMLButtonElement | null>; // Allow null for the ref's current property
}

const FilterSidebarOffcanvas: React.FC<FilterSidebarOffcanvasProps> = ({
  isOpen,
  onClose,
  idPrefix,
  triggerRef,
}) => {
  const portalRoot = document.getElementById(
    "filter-sidebar-mobile-portal-root"
  );
  const { t, ready: areTranslationsReady } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  // --- Focus and Body Class Management Effect ---
  useEffect(() => {
    const closeButton = panelRef.current?.querySelector<HTMLButtonElement>(
      ".offcanvas-close-btn"
    );

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.body.classList.add("mobile-filter-open");
      setTimeout(() => closeButton?.focus(), 100);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.classList.remove("mobile-filter-open");
        document.removeEventListener("keydown", handleKeyDown);
        triggerRef.current?.focus();
      };
    }
  }, [isOpen, onClose, triggerRef]);

  if (!portalRoot) {
    console.error(
      "[FilterSidebarOffcanvas] Portal root #filter-sidebar-mobile-portal-root not found in the DOM."
    );
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={`offcanvas-wrapper filter-sidebar-offcanvas-wrapper ${
        isOpen ? "is-open" : ""
      }`}
      aria-hidden={!isOpen}
    >
      <div
        className="offcanvas-overlay filter-sidebar-offcanvas__overlay"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="offcanvas-panel filter-sidebar-offcanvas__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-filter-title"
      >
        <div className="offcanvas-header filter-sidebar-offcanvas__header">
          <h2
            id="mobile-filter-title"
            className="offcanvas-title filter-sidebar-offcanvas__title"
          >
            {areTranslationsReady ? (
              t("productsArchive.sidebar.filtersTitle")
            ) : (
              <Skeleton width={100} />
            )}
          </h2>
          <button
            onClick={onClose}
            className="offcanvas-close-btn filter-sidebar-offcanvas__close-btn"
            aria-label="Close filters"
          >
            <Icon iconName="x-mark" className="icon--stroke-thickest" />
          </button>
        </div>
        <div className="offcanvas-body filter-sidebar-offcanvas__body">
          <FilterSidebar idPrefix={idPrefix} />
        </div>
      </div>
    </div>,
    portalRoot
  );
};

export default FilterSidebarOffcanvas;
